import React, { useState, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import PDFViewer from '../components/puzzle-pdf/PDFViewer';
import PuzzleWorkspace from '../components/puzzle-pdf/PuzzleWorkspace';
import PieceCropEditor from '../components/puzzle-pdf/PieceCropEditor';
import { useLanguage } from '../contexts/LanguageContext';
import '../puzzleApp.css';

function PuzzlePdf() {
  const { t } = useLanguage();
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [activeTab, setActiveTab] = useState('pdf');
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfData, setPdfData] = useState(null); // Store raw PDF data
  const [pdfSource, setPdfSource] = useState('file'); // 'file' or 'url'
  const [urlParamLoaded, setUrlParamLoaded] = useState(false);
  const [isBookmarkMode, setIsBookmarkMode] = useState(false);
  const [showCropEditor, setShowCropEditor] = useState(false);

  // Fallback URLs to try if the first one fails
  const fallbackUrls = [
    'https://app.rowcounterapp.com/generated_pdfs/pattern_qqqvmakuktnxalcj_120_newborn9.pdf',
    'https://cors-anywhere.herokuapp.com/https://app.rowcounterapp.com/generated_pdfs/pattern_qqqvmakuktnxalcj_120_newborn9.pdf',
    'https://mozilla.github.io/pdf.js/web/compressed.tracemonkey-pldi-09.pdf' // Test PDF
  ];

  // Helper function to validate PDF URLs
  const isValidPdfUrl = (url) => {
    try {
      new URL(url);
      return url.toLowerCase().includes('.pdf') || url.includes('pdf');
    } catch {
      return false;
    }
  };

  // Check for URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Function to process base64 PDF data from React Native
    const processRNPdfData = () => {
      if (!window.__RN_PDF_BASE64__) {
        console.log('⏳ Waiting for __RN_PDF_BASE64__ to be set...');
        return false;
      }

      console.log('📱 Android base64 PDF data detected, length:', window.__RN_PDF_BASE64__.length);

      try {
        // Convert base64 to Uint8Array
        const binaryString = atob(window.__RN_PDF_BASE64__);
        console.log('📱 Decoded base64, binary length:', binaryString.length);

        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        console.log('📱 Created Uint8Array from base64, size:', bytes.length);

        setPdfData(bytes); // Store raw data
        setPdfSource('file');
        setPdfFile(null); // Clear file object as we have raw data
        setUrlParamLoaded(true);
        setActiveTab('pdf');
        console.log('✅ PDF loaded from Android base64 injection (raw data)');

        // Clean up global variable
        delete window.__RN_PDF_BASE64__;
        return true;
      } catch (error) {
        console.error('❌ Error processing base64 PDF:', error);
        alert('Failed to process PDF data: ' + error.message);
        return false;
      }
    };

    // Check for base64 PDF data injected by React Native (Android)
    let cleanupPolling = () => { };

    if (window.isReactNativeWebView) {
      console.log('🔍 In React Native WebView, checking for PDF data...');

      // Always check URL parameters first (including bookmark mode)
      checkUrlParameters();

      // Try immediately
      if (!processRNPdfData()) {
        // If not available yet, poll for it (injectedJavaScript runs after page load)
        let attempts = 0;
        const checkInterval = setInterval(() => {
          attempts++;
          console.log('🔍 Polling attempt', attempts, 'for __RN_PDF_BASE64__');

          if (processRNPdfData()) {
            clearInterval(checkInterval);
            console.log('✅ Found PDF data after', attempts, 'attempts');
          } else if (attempts > 20) {
            // Stop after 2 seconds (20 * 100ms)
            clearInterval(checkInterval);
            console.log('⏹️ Stopped polling for PDF data');
          }
        }, 100);

        cleanupPolling = () => clearInterval(checkInterval);
      }
    } else {
      checkUrlParameters();
    }

    // Function to check URL parameters for PDF
    function checkUrlParameters() {
      // Check for bookmark mode
      const bookmarkParam = urlParams.get('bookmark');
      if (bookmarkParam === '1') {
        console.log('📌 Bookmark mode detected');
        setIsBookmarkMode(true);
      }

      // Support multiple parameter names: pdf, pdfUrl, url
      const pdfParam = urlParams.get('pdf') ||
        urlParams.get('pdfUrl') ||
        urlParams.get('url');

      if (pdfParam) {
        console.log('PDF URL parameter detected:', pdfParam);

        // Validate the URL before setting it
        if (isValidPdfUrl(pdfParam)) {
          setPdfUrl(pdfParam);
          setPdfSource('url');
          setPdfFile(null);
          setPdfData(null); // Clear raw data if URL is used
          setUrlParamLoaded(true);
          // Switch to the PDF tab automatically
          setActiveTab('pdf');
          console.log('✅ PDF URL loaded from parameter');
        } else {
          console.warn('⚠️ Invalid PDF URL in parameter:', pdfParam);
          // Could show a user-friendly error message here if needed
        }
      }
    }

    // Set up handler for React Native PDF upload
    window.handlePdfUploadFromReactNative = async (data) => {
      console.log('📱 Handling PDF upload from React Native');

      try {
        let bytes;

        // Check if data is already a Uint8Array
        if (data instanceof Uint8Array) {
          console.log('📱 Received Uint8Array directly, size:', data.length);
          bytes = data;
        }
        // Check if data is a File object
        else if (data && data.type === 'application/pdf') {
          console.log('📱 Received File object:', data.name, 'size:', data.size);
          const arrayBuffer = await data.arrayBuffer();
          bytes = new Uint8Array(arrayBuffer);
        }
        else {
          console.error('❌ Invalid data received from React Native:', typeof data);
          return;
        }

        setPdfData(bytes);
        setPdfSource('file');
        // We don't need a file object or URL if we have raw data
        setPdfFile(null);
        setPdfUrl(null);

        // Switch to the PDF tab automatically
        setActiveTab('pdf');
        console.log('✅ PDF loaded from React Native (raw data)');
      } catch (e) {
        console.error('Error processing PDF data:', e);
        alert('Error processing PDF data: ' + e.message);
      }
    };

    // Signal to React Native that we're ready
    console.log('🟢 handlePdfUploadFromReactNative is now available');
    console.log('🔍 window.isReactNativeWebView:', window.isReactNativeWebView);
    console.log('🔍 window.ReactNativeWebView:', typeof window.ReactNativeWebView);

    // If we're in a WebView, notify that we're ready
    if (window.isReactNativeWebView && window.ReactNativeWebView) {
      console.log('📱 Notifying React Native that handler is ready');
      try {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'WEB_APP_READY'
        }));
        console.log('✅ WEB_APP_READY message sent successfully');
      } catch (error) {
        console.error('❌ Error sending WEB_APP_READY message:', error);
      }
    } else {
      console.log('⚠️ Not in React Native WebView or ReactNativeWebView not available');
    }

    // Cleanup
    return () => {
      cleanupPolling();
      delete window.handlePdfUploadFromReactNative;
    };
  }, []); // Empty dependency array means this runs once on mount

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setPdfSource('file');
      setPdfData(null); // Clear raw data if file is used
      // Create object URL for the file
      const fileUrl = URL.createObjectURL(file);
      setPdfUrl(fileUrl);
    } else {
      alert('Please select a PDF file');
    }
  };

  const handleUrlChange = (url) => {
    setPdfUrl(url);
    setPdfSource('url');
    setPdfFile(null);
    setPdfData(null); // Clear raw data if URL is used
    setUrlParamLoaded(false); // Reset parameter loaded state for manual changes
  };

  // In bookmark mode, automatically show crop editor when one area is selected
  useEffect(() => {
    if (isBookmarkMode && selectedAreas.length === 1 && !showCropEditor) {
      console.log('📌 Bookmark mode: One area selected, showing crop editor');
      setShowCropEditor(true);
    }
  }, [isBookmarkMode, selectedAreas, showCropEditor]);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="App">
        <header className="app-header">
          <h1>{isBookmarkMode ? (t('createBookmark') || 'Create Bookmark') : t('appTitle')}</h1>
          {/* Hide tab navigation in bookmark mode */}
          {!isBookmarkMode && (
            <div className="tab-navigation">
              <button
                className={`tab ${activeTab === 'pdf' ? 'active' : ''}`}
                onClick={() => setActiveTab('pdf')}
              >
                {t('tabSelectAreas')} ({selectedAreas.length})
              </button>
              <button
                className={`tab ${activeTab === 'puzzle' ? 'active' : ''}`}
                onClick={() => setActiveTab('puzzle')}
              >
                {t('tabCompose')}
              </button>
            </div>
          )}
        </header>

        <main className="app-content">
          {activeTab === 'pdf' && (
            <div className="tab-content">
              {/* Show PDF source controls only when no PDF is loaded */}
              {!pdfUrl && !pdfFile && !pdfData && (
                <div className="pdf-url-controls">
                  <div>
                    <label htmlFor="pdf-upload" style={{
                      display: 'inline-block',
                      padding: '12px 24px',
                      border: '1px solid #2ba5f7',
                      borderRadius: '24px',
                      fontSize: '1rem',
                      cursor: 'pointer',
                      color: '#2ba5f7',
                    }}>{t('uploadPdfFile')}</label>
                    <input
                      id="pdf-upload"
                      class="form-control"
                      placeholder="Enter email"
                      type="file"
                      accept=".pdf"
                      onChange={handleFileUpload}
                      style={{
                        display: "none"
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Show PDF viewer and related content only when PDF is loaded */}
              {(pdfUrl || pdfFile || pdfData) && (
                <>
                  <PDFViewer
                    pdfUrl={pdfUrl}
                    pdfData={pdfData} // Pass raw data
                    selectedPieces={selectedAreas}
                    setSelectedPieces={setSelectedAreas}
                    onError={(error) => console.error('PDF Error:', error)}
                    isBookmarkMode={isBookmarkMode}
                  />
                </>
              )}
            </div>
          )}

          {activeTab === 'puzzle' && (
            <div className="tab-content">
              <PuzzleWorkspace selectedPieces={selectedAreas} setSelectedPieces={setSelectedAreas} />
            </div>
          )}
        </main>

        {/* Show crop editor in bookmark mode when an area is selected */}
        {isBookmarkMode && showCropEditor && selectedAreas.length > 0 && (
          <PieceCropEditor
            piece={selectedAreas[0]}
            isBookmarkMode={true}
            onSave={(croppedPiece) => {
              console.log('📌 Bookmark mode: Exporting cropped image');
              // Export the cropped image back to React Native
              if (window.ReactNativeWebView) {
                try {
                  window.ReactNativeWebView.postMessage(JSON.stringify({
                    type: 'EXPORT_IMAGE',
                    imageData: croppedPiece.extractedImage
                  }));
                  console.log('✅ Image exported to React Native');
                } catch (error) {
                  console.error('❌ Error sending image to React Native:', error);
                  alert('Failed to export image: ' + error.message);
                }
              } else {
                console.warn('⚠️ ReactNativeWebView not available');
                alert('Not running in React Native WebView');
              }
            }}
            onCancel={() => {
              console.log('📌 Bookmark mode: Crop cancelled');
              setShowCropEditor(false);
              setSelectedAreas([]);
            }}
          />
        )}
      </div>
    </DndProvider>
  );
}

export default PuzzlePdf;
